/**
 * Certificate Dashboard Component
 *
 * Shows all certificates across the fleet with expiry tracking.
 * Part of the Fleet Dashboard.
 */

import React, { useState, useMemo } from 'react';
import {
  FileCheck,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Calendar,
  Ship,
  Filter,
  ChevronDown,
  ChevronUp,
  Clock,
  Search
} from 'lucide-react';

import { useFleetData } from '../../../../hooks/domain/fleet/useFleetData';

/**
 * Status badge for certificate
 */
const CertificateStatus = ({ status, daysUntilExpiry }) => {
  const config = {
    expired: {
      icon: AlertTriangle,
      bg: 'bg-red-100',
      text: 'text-red-700',
      label: 'Expired'
    },
    critical: {
      icon: AlertTriangle,
      bg: 'bg-red-100',
      text: 'text-red-700',
      label: `${daysUntilExpiry}d left`
    },
    warning: {
      icon: AlertCircle,
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      label: `${daysUntilExpiry}d left`
    },
    valid: {
      icon: CheckCircle,
      bg: 'bg-green-100',
      text: 'text-green-700',
      label: 'Valid'
    }
  };

  const { icon: Icon, bg, text, label } = config[status] || config.valid;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

/**
 * Calculate certificate status based on expiry date
 */
const getCertificateStatus = (expiryDate) => {
  if (!expiryDate) return { status: 'valid', daysUntilExpiry: null };

  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { status: 'expired', daysUntilExpiry: diffDays };
  if (diffDays <= 14) return { status: 'critical', daysUntilExpiry: diffDays };
  if (diffDays <= 60) return { status: 'warning', daysUntilExpiry: diffDays };
  return { status: 'valid', daysUntilExpiry: diffDays };
};

/**
 * Single Certificate Row
 */
const CertificateRow = ({ cert, expanded, onToggle }) => {
  const { status, daysUntilExpiry } = getCertificateStatus(cert.expiryDate);

  return (
    <div className={`
      bg-white border rounded-lg overflow-hidden
      ${status === 'expired' ? 'border-red-200' :
        status === 'critical' ? 'border-red-200' :
        status === 'warning' ? 'border-amber-200' : 'border-slate-200'}
    `}>
      {/* Main Row */}
      <div
        className={`px-4 py-3 flex items-center gap-4 cursor-pointer hover:bg-slate-50
          ${status === 'expired' || status === 'critical' ? 'bg-red-50' :
            status === 'warning' ? 'bg-amber-50' : ''}`}
        onClick={onToggle}
      >
        {/* Certificate Icon */}
        <div className={`p-2 rounded-lg
          ${status === 'expired' || status === 'critical' ? 'bg-red-100' :
            status === 'warning' ? 'bg-amber-100' : 'bg-slate-100'}`}>
          <FileCheck className={`h-5 w-5
            ${status === 'expired' || status === 'critical' ? 'text-red-600' :
              status === 'warning' ? 'text-amber-600' : 'text-slate-600'}`} />
        </div>

        {/* Certificate Info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-slate-800 truncate">{cert.name}</h4>
          <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
            <span className="flex items-center gap-1">
              <Ship className="h-3 w-3" />
              {cert.vesselName}
            </span>
            {cert.certificateNumber && (
              <span className="text-slate-400">#{cert.certificateNumber}</span>
            )}
          </div>
        </div>

        {/* Expiry Date */}
        <div className="text-right mr-4">
          <p className="text-sm font-medium text-slate-700">
            {cert.expiryDate
              ? new Date(cert.expiryDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })
              : 'No expiry'
            }
          </p>
          <CertificateStatus status={status} daysUntilExpiry={daysUntilExpiry} />
        </div>

        {/* Expand Toggle */}
        {expanded ? (
          <ChevronUp className="h-5 w-5 text-slate-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-400" />
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Issued By</p>
              <p className="font-medium">{cert.issuedBy || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-slate-500">Issue Date</p>
              <p className="font-medium">
                {cert.issueDate
                  ? new Date(cert.issueDate).toLocaleDateString('en-GB')
                  : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Type</p>
              <p className="font-medium">{cert.type || 'Certificate'}</p>
            </div>
            <div>
              <p className="text-slate-500">Certificate Number</p>
              <p className="font-medium">{cert.certificateNumber || 'N/A'}</p>
            </div>
          </div>
          {cert.notes && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-slate-500 text-sm">Notes</p>
              <p className="text-sm mt-1">{cert.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Summary Stats Bar
 */
const CertificateSummary = ({ certificates }) => {
  const stats = useMemo(() => {
    let expired = 0;
    let critical = 0;
    let warning = 0;
    let valid = 0;

    certificates.forEach(cert => {
      const { status } = getCertificateStatus(cert.expiryDate);
      if (status === 'expired') expired++;
      else if (status === 'critical') critical++;
      else if (status === 'warning') warning++;
      else valid++;
    });

    return { expired, critical, warning, valid, total: certificates.length };
  }, [certificates]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 rounded-lg">
            <FileCheck className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Total</p>
            <p className="text-lg font-semibold text-slate-800">{stats.total}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Expired</p>
            <p className={`text-lg font-semibold ${stats.expired > 0 ? 'text-red-600' : 'text-slate-800'}`}>
              {stats.expired}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <Clock className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Critical (&lt;14d)</p>
            <p className={`text-lg font-semibold ${stats.critical > 0 ? 'text-red-600' : 'text-slate-800'}`}>
              {stats.critical}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <AlertCircle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Warning (&lt;60d)</p>
            <p className={`text-lg font-semibold ${stats.warning > 0 ? 'text-amber-600' : 'text-slate-800'}`}>
              {stats.warning}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-slate-500">Valid</p>
            <p className="text-lg font-semibold text-green-600">{stats.valid}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Certificate Dashboard Main Component
 */
const CertificateDashboard = () => {
  const { allCertificates, isLoading } = useFleetData();
  const [filter, setFilter] = useState('all'); // all, expired, expiring, valid
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [sortBy, setSortBy] = useState('expiry'); // expiry, vessel, name

  // Filter and sort certificates
  const filteredCertificates = useMemo(() => {
    let result = allCertificates || [];

    // Apply status filter
    if (filter !== 'all') {
      result = result.filter(cert => {
        const { status } = getCertificateStatus(cert.expiryDate);
        if (filter === 'expired') return status === 'expired';
        if (filter === 'expiring') return status === 'critical' || status === 'warning';
        if (filter === 'valid') return status === 'valid';
        return true;
      });
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(cert =>
        cert.name?.toLowerCase().includes(term) ||
        cert.vesselName?.toLowerCase().includes(term) ||
        cert.certificateNumber?.toLowerCase().includes(term)
      );
    }

    // Apply sort
    result = [...result].sort((a, b) => {
      if (sortBy === 'expiry') {
        if (!a.expiryDate) return 1;
        if (!b.expiryDate) return -1;
        return new Date(a.expiryDate) - new Date(b.expiryDate);
      }
      if (sortBy === 'vessel') {
        return (a.vesselName || '').localeCompare(b.vesselName || '');
      }
      return (a.name || '').localeCompare(b.name || '');
    });

    return result;
  }, [allCertificates, filter, searchTerm, sortBy]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-24 bg-slate-200 rounded-xl mb-6" />
          <div className="space-y-3">
            <div className="h-16 bg-slate-200 rounded-lg" />
            <div className="h-16 bg-slate-200 rounded-lg" />
            <div className="h-16 bg-slate-200 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Summary Stats */}
      <CertificateSummary certificates={allCertificates || []} />

      {/* Filters Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search certificates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500"
            >
              <option value="all">All Certificates</option>
              <option value="expired">Expired</option>
              <option value="expiring">Expiring Soon</option>
              <option value="valid">Valid</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500"
            >
              <option value="expiry">Sort by Expiry</option>
              <option value="vessel">Sort by Vessel</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
        </div>
      </div>

      {/* Certificate List */}
      {filteredCertificates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <FileCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">No Certificates Found</h3>
          <p className="text-slate-500">
            {searchTerm || filter !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'No certificates have been added to the fleet yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCertificates.map(cert => (
            <CertificateRow
              key={cert.id}
              cert={cert}
              expanded={expandedId === cert.id}
              onToggle={() => setExpandedId(
                expandedId === cert.id ? null : cert.id
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CertificateDashboard;
